import axiosInstance from "./axiosInstance";
import { User, UserRole } from "common/models/types";
import { log, LogLevel } from "utils/LogUtil";

interface ApiResponse<T> {
  data: T;
  error: number;
  message?: string;
  meta?: {
    total?: number;
    hasMore?: boolean;
  };
}

// 사용자 목록 조회 API 호출
// search: 이름·전화는 정확 일치(암호문), 이메일은 부분 일치(LIKE), UUID/ymyd_id 매칭 (백엔드 단일 파라미터)
export const fetchUsers = async (params?: {
  offset?: number;
  limit?: number;
  role?: string;
  search?: string;
}): Promise<ApiResponse<User[]>> => {
  const response = await axiosInstance.get<ApiResponse<User[]>>("/api/v1/users", {
    params,
  });
  return response.data;
};

// 사용자 상세 정보 조회 API 호출
export const fetchUserById = async (userId: string): Promise<User> => {
  const response = await axiosInstance.get<ApiResponse<User>>(`/api/v1/users/${userId}`);
  return response.data.data;
};

// 사용자 생성 API 호출
export const createUser = async (user: Partial<User>): Promise<User> => {
  const response = await axiosInstance.post<ApiResponse<User>>("/api/v1/users", user);
  return response.data.data;
};

// 사용자 업데이트 API 호출
export const updateUser = async (userId: string, userData: Partial<User>): Promise<User> => {
  const response = await axiosInstance.put<ApiResponse<User>>(`/api/v1/users/${userId}`, userData);
  return response.data.data;
};

// 사용자 삭제 API 호출 (실제 삭제가 아닌 soft delete)
export const deleteUser = async (userId: string): Promise<void> => {
  await axiosInstance.delete(`/api/v1/users/${userId}`);
};

// 사용자 저장 (생성 또는 업데이트) API 호출
export const saveUser = async (user: Partial<User>): Promise<User> => {
  // 새 사용자 (id가 없는 경우)
  if (!user.id) {
    return createUser(user);
  }
  // 기존 사용자 업데이트
  else {
    return updateUser(user.id, user);
  }
};

// 대량 사용자 생성 API 호출
export const createBulkUsers = async (
  file: File
): Promise<{
  error: number;
  message: string;
  data?: {
    successCount: number;
    failureCount: number;
    failures?: Array<{
      row: number;
      data: {
        company: string;
        name: string;
        license: string;
        phone: string;
        email: string;
      };
      reason: string;
    }>;
  };
}> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axiosInstance.post("/api/v1/users/bulk", formData, {
    timeout: 300000, // 5분 타임아웃
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};
